<template>
  <div>
    <div class="container pt-4 pt-sm-3 pb-3">
      <div class="row">
        <div class="col-lg-8 offset-lg-2 col-xl-6 offset-xl-3">
          <h1 class="h5">
            <router-link v-if="newTireReport" :to="{ name: 'trailer', params: { id: newTireReport.tireReportId } }">
              <i class="far fa-lg fa-angle-left mr-2"></i>Workshop</router-link>
          </h1>
          <hr />

          <div class="form-group">
            <label>
              Workshop
              <span class="text-danger">*</span>
            </label>
            <v-select
              :options="workshops"
              :filter-by="filterBy"
              :reduce="(workshop) => workshop.workshopId"
              v-model="formdata.workshopId"
              class="workshop-select"
            >
              <template v-slot:option="workshop">
                {{ workshop.name }}
                <small class="">({{ workshop.city }}, {{ workshop.countryCode }})</small>
              </template>
            </v-select>
            <small class="form-text text-muted">Type workshop name and select from list.</small>
          </div>
          <div class="form-group">
            <div class="form-row">
              <div class="col-6 col-sm-5 col-md-4">
                <label>
                  Date
                  <span class="text-danger">*</span>
                </label>
                <date-picker
                  updateOnScroll
                  v-model="formdata.date"
                  :minDate="false"
                  :clearButton="true"
                  inputClass="text-left"
                >
                  <template slot="prepend">
                    <span class="input-group-text">
                      <i class="far fa-calendar-alt"></i>
                    </span>
                  </template>
                </date-picker>
              </div>
              <div class="col-6 col-sm-7 col-md-8">
                <label>Fitter</label>
                <input type="text" class="form-control" v-model="formdata.fitterName" />
                <small class="form-text text-muted">Name of person who performed the fitting.</small>
              </div>
            </div>
          </div>

          <div class="form-group">
            <div class="form-group">
              <label>Original tire report</label>
              <div class="custom-file">
                <input type="file" class="custom-file-input" @change="addReport" id="add-report" />
                <label class="custom-file-label grey" for="add-report">
                  <span v-if="tireReport">{{ tireReport.file.name }}</span>
                  <span v-else>Choose file</span>
                </label>
                <div v-if="tireReport && tireReport.progress.uploading" class="mt-2 progress" style="height: 10px;">
                  <div
                    class="progress-bar"
                    role="progressbar"
                    :style="'width: ' + tireReport.progress.current + '%;'"
                    :aria-valuenow="tireReport.progress.current"
                    aria-valuemin="0"
                    aria-valuemax="100"
                  >
                    {{ tireReport.progress.current }}%
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <div class="form-group">
              <label>Invoice</label>
              <div class="custom-file">
                <input type="file" class="custom-file-input" @change="addInvoice" id="add-invoice" />
                <label class="custom-file-label grey" for="add-invoice">
                  <span v-if="invoice">{{ invoice.file.name }}</span>
                  <span v-else>Choose file</span>
                </label>
                <div v-if="invoice && invoice.progress.uploading" class="mt-2 progress" style="height: 10px;">
                  <div
                    class="progress-bar"
                    role="progressbar"
                    :style="'width: ' + invoice.progress.current + '%;'"
                    :aria-valuenow="invoice.progress.current"
                    aria-valuemin="0"
                    aria-valuemax="100"
                  >
                    {{ invoice.progress.current }}%
                  </div>
                </div>
              </div>
            </div>
          </div>
          <button @click="save" tag="button" :disabled="!canSave" class="btn btn-info btn-lg btn-block mt-4">
            Save details
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.workshop-select .vs__search::placeholder,
.workshop-select .vs__dropdown-toggle,
.workshop-select .vs__dropdown-menu {
  background: white;
  color: #394066;
}
</style>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'
import {v4 as uuidv4} from 'uuid'
import 'vue-select/dist/vue-select.css'
import { uploadAttachment } from '../../api/api'

export default {
  metaInfo: {
    title: 'Workshop',
  },
  data: () => {
    return {
      formdata: {
        workshopId: '',
        date: '',
        fitterName: '',
      },
      invoice: null,
      tireReport: null,
      search: {
        q: '',
        savedSearch: '',
        countryCode: '',
        hasContryCode: false,
        parts: [],
      },
    }
  },
  mounted() {
     if (!this.newTireReport || !this.newTireReport.tireReportId) {
      if (this.$route.params.id) {
         this.$router.push({ name: "trailer", params: { id: this.$route.params.id } })
      } else {
        this.$router.replace({ name: "frontpage" });
      }
      return;
    }

    if (!(this.newTireReport && this.newTireReport.workshop)) {
      return
    }

    const w = this.newTireReport.workshop
    this.formdata.workshopId = w.workshopId
    this.formdata.date = w.date
    this.formdata.fitterName = w.fitterName

    const a = this.newTireReport.attachments
    this.tireReport = a && a.tireReport ? a.tireReport : null
    this.invoice = a && a.invoice ? a.invoice : null
  },
  computed: {
    ...mapGetters('tireReports', ['workshops']),
    ...mapState('tireReports', ['newTireReport']),
    countryCodes: function () {
      let codes = {}
      this.workshops.forEach((workshop) => {
        codes[workshop.countryCode] = true
      })
      return codes
    },
    canSave: function () {
      if (!this.formdata.workshopId) return false
      if (!this.formdata.date) return false
      if (this.invoice && this.invoice.progress.uploading) return false
      if (this.tireReport && this.tireReport.progress.uploading) return false
      return true
    },
  },
  methods: {
    ...mapActions('tireReports', ['addWorkshopData', 'addAttachmentData']),
    filterBy: function (option, label, search) {
      let saved = this.search
      if (saved.savedSearch != search) {
        saved.savedSearch = search
        saved.q = search.toLowerCase()
        saved.hasContryCode = false
        saved.countryCode = ''

        saved.q.split().forEach((part) => {
          let cc = part.toUpperCase()
          if (this.countryCodes[cc]) {
            saved.hasContryCode = true
            saved.countryCode = cc
          }
        })
      }

      return (
        option.searchLabel.indexOf(saved.q) > -1 || (saved.hasContryCode && option.countryCode == saved.countryCode)
      )
    },
    addReport: function (ev) {
      this.uploadFile(ev, 'tireReport', 'service-report')
    },
    addInvoice: function (ev) {
      this.uploadFile(ev, 'invoice', 'service-report-invoice')
    },
    uploadFile(ev, key, label) {
      if (ev.target.files && ev.target.files[0]) {
        const file = ev.target.files[0]
        const fileObject = {
          file: event.target.files[0],
          blob: file.type.substring(0, 5) == 'image' ? URL.createObjectURL(file) : null,
        }

        fileObject.file.id = uuidv4()
        this.$set(fileObject, 'progress', {
          current: 0,
          uploading: true,
          id: null,
        })
        this[key] = Object.assign({}, this[key], fileObject)

        if (file.size > 1024 * 1024 * 100) {
          fileObject.progress.error = 'too large'
          fileObject.progress.uploading = false
          return
        }

        uploadAttachment(fileObject, this.newTireReport.tireReportId, key, label).then(() => {})
      } else {
        this[key] = null
      }
    },
    save() {
      this.addWorkshopData({
        workshopId: this.formdata.workshopId,
        date: this.formdata.date.split('T')[0],
        fitterName: this.formdata.fitterName,
      })
      this.addAttachmentData({
        ...(this.invoice && { invoice: this.invoice }),
        ...(this.tireReport && { tireReport: this.tireReport }),
      })
      this.$router.push({
        name: 'trailer',
        params: { id: this.newTireReport.tireReportId },
      })
    },
  },
}
</script>
